import { useCartManagement } from "@/hooks/useCartManagement";
import { useProductManagement } from "@/hooks/useProductManagement";
import { useButtonVisibility } from "@/hooks/useButtonVisibility";
import { ButtonVisibilityToggle } from "./ButtonVisibilityToggle";
import { StockHeader } from "./StockHeader";
import { DialogManager } from "./dialog/DialogManager";
import { InventoryManager } from "./inventory/InventoryManager";
import { useStockDialogs } from "./hooks/useStockDialogs";
import { useInventoryState } from "./hooks/useInventoryState";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { HelpCircle } from "lucide-react";
import { useState } from "react";
interface StockContentProps {
  stockItems: any[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  refetch: () => void;
  onInventoryStateChange: (isActive: boolean) => void;
}
export const StockContent = ({
  stockItems,
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  refetch,
  onInventoryStateChange
}: StockContentProps) => {
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const {
    buttonVisibility,
    handleToggleVisibility
  } = useButtonVisibility();
  const {
    isCartDialogOpen,
    setIsCartDialogOpen,
    handleAddToCart
  } = useCartManagement(refetch);
  const {
    isAddProductDialogOpen,
    setIsAddProductDialogOpen,
    isDeleteProductDialogOpen,
    setIsDeleteProductDialogOpen,
    handleAddProduct,
    handleDeleteProduct
  } = useProductManagement(refetch);
  const {
    isRestockDialogOpen,
    setIsRestockDialogOpen,
    isLossDialogOpen,
    setIsLossDialogOpen,
    isReturnDialogOpen,
    setIsReturnDialogOpen,
    selectedItem,
    handleRestockClick,
    handleReturnClick,
    handleLossClick
  } = useStockDialogs();
  const {
    isInventoryActive,
    setIsInventoryActive,
    handleInventoryComplete
  } = useInventoryState(onInventoryStateChange);
  return <div className="relative space-y-4">
      <div className="flex justify-between items-center">
        <Button variant="ghost" onClick={() => setIsHelpDialogOpen(true)} className="h-8 flex items-center gap-1 px-2 bg-amber-100">
          <HelpCircle className="h-4 w-4" />
          <span className="text-sm text-muted-foreground">Comment ça marche ?</span>
        </Button>
        <ButtonVisibilityToggle buttonTypes={[{
        id: 'cart',
        label: 'Bouton Panier',
        isVisible: buttonVisibility.cart
      }, {
        id: 'restock',
        label: 'Bouton Réapprovisionnement',
        isVisible: buttonVisibility.restock
      }, {
        id: 'return',
        label: 'Bouton Retour',
        isVisible: buttonVisibility.return
      }, {
        id: 'quantity',
        label: 'Champ Quantité',
        isVisible: buttonVisibility.quantity
      }, {
        id: 'actions',
        label: 'Autres Actions',
        isVisible: buttonVisibility.actions
      }, {
        id: 'inventory',
        label: 'Bouton Inventaire',
        isVisible: buttonVisibility.inventory
      }, {
        id: 'loss',
        label: 'Bouton Perte',
        isVisible: buttonVisibility.loss
      }, {
        id: 'delete',
        label: 'Bouton Supprimer',
        isVisible: buttonVisibility.delete
      }, {
        id: 'add',
        label: 'Bouton Ajouter',
        isVisible: buttonVisibility.add
      }]} onToggle={handleToggleVisibility} />
      </div>

      <Dialog open={isHelpDialogOpen} onOpenChange={setIsHelpDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Guide de gestion du stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <h3 className="font-semibold mb-2">1. Gestion des produits</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-sm">Ajouter un produit</h4>
                  <p className="text-sm text-muted-foreground">
                    Utilisez le bouton "Ajouter" pour créer un nouveau produit dans votre stock. 
                    Remplissez les informations requises comme le nom, le prix, la quantité.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Réapprovisionner</h4>
                  <p className="text-sm text-muted-foreground">
                    Le bouton "Réapprovisionner" permet d'ajouter des quantités à un produit existant.
                    Cette action sera visible dans l'historique des mouvements.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Supprimer</h4>
                  <p className="text-sm text-muted-foreground">
                    Supprime définitivement un produit du stock. À utiliser avec précaution.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">2. Ventes et Panier</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-sm">Ajouter au panier</h4>
                  <p className="text-sm text-muted-foreground">
                    Sélectionnez les produits et les quantités à vendre. Une fois dans le panier, vous pourrez :
                  </p>
                  <ul className="list-disc list-inside mt-1 text-sm text-muted-foreground">
                    <li>Finaliser la vente normale</li>
                    <li>Faire une vente à crédit</li>
                    <li>Ajouter la signature du client si nécessaire</li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-1">
                    Après la vente, consultez l'onglet "Historique" pour voir la transaction.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">3. Retours et Pertes</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-sm">Gérer un retour</h4>
                  <p className="text-sm text-muted-foreground">
                    Pour les produits retournés par les clients :
                  </p>
                  <ul className="list-disc list-inside mt-1 text-sm text-muted-foreground">
                    <li>Utilisez le bouton "Retour"</li>
                    <li>Spécifiez la quantité et la raison</li>
                    <li>Vérifiez ensuite dans l'onglet "Retours" pour le suivi</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-sm">Enregistrer une perte</h4>
                  <p className="text-sm text-muted-foreground">
                    Pour les produits perdus ou endommagés :
                  </p>
                  <ul className="list-disc list-inside mt-1 text-sm text-muted-foreground">
                    <li>Utilisez le bouton "Perte"</li>
                    <li>Documentez la raison et ajoutez des photos si nécessaire</li>
                    <li>Consultez l'onglet "Pertes" pour le suivi</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">4. Inventaire</h3>
              <p className="text-sm text-muted-foreground">
                L'inventaire permet de vérifier et ajuster vos stocks :
              </p>
              <ul className="list-disc list-inside mt-2 text-sm text-muted-foreground">
                <li>Lancez un inventaire avec le bouton "Démarrer l'inventaire"</li>
                <li>Comptez physiquement vos produits</li>
                <li>Comparez avec les quantités du système</li>
                <li>Validez l'inventaire une fois terminé</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                Les différences d'inventaire seront visibles dans l'onglet "Personnel" pour validation.
              </p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold mb-2 text-yellow-800">Suivi et Validation</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
                <li>Historique : Consultez toutes les transactions dans l'onglet "Historique"</li>
                <li>Retours : Suivez les retours clients dans l'onglet "Retours"</li>
                <li>Pertes : Gérez les pertes dans l'onglet "Pertes"</li>
                <li>Personnel : Validez les différences d'inventaire</li>
                <li>Comptage : Validez les montants pour le chiffre d'affaires</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsHelpDialogOpen(false)}>J'ai compris</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StockHeader searchQuery={searchQuery} setSearchQuery={setSearchQuery} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} onOpenAddDialog={() => setIsAddProductDialogOpen(true)} onOpenDeleteDialog={() => setIsDeleteProductDialogOpen(true)} onOpenCart={() => setIsCartDialogOpen(true)} onOpenLossDialog={handleLossClick} stockItems={stockItems} onRestockClick={handleRestockClick} refetch={refetch} onInventoryStart={() => setIsInventoryActive(true)} onInventoryComplete={handleInventoryComplete} isInventoryActive={isInventoryActive} buttonVisibility={buttonVisibility} />

      <InventoryManager isInventoryActive={isInventoryActive} stockItems={stockItems} buttonVisibility={buttonVisibility} refetch={refetch} onRestockClick={handleRestockClick} onReturnClick={handleReturnClick} />

      <DialogManager isAddProductDialogOpen={isAddProductDialogOpen} setIsAddProductDialogOpen={setIsAddProductDialogOpen} isDeleteProductDialogOpen={isDeleteProductDialogOpen} setIsDeleteProductDialogOpen={setIsDeleteProductDialogOpen} isCartDialogOpen={isCartDialogOpen} setIsCartDialogOpen={setIsCartDialogOpen} isRestockDialogOpen={isRestockDialogOpen} setIsRestockDialogOpen={setIsRestockDialogOpen} isReturnDialogOpen={isReturnDialogOpen} setIsReturnDialogOpen={setIsReturnDialogOpen} isLossDialogOpen={isLossDialogOpen} setIsLossDialogOpen={setIsLossDialogOpen} selectedItem={selectedItem} stockItems={stockItems} handleAddProduct={handleAddProduct} handleDeleteProduct={handleDeleteProduct} refetch={refetch} />
    </div>;
};